import type { z } from "zod/v4";
import type {
  VizDataset,
  VizViewType,
} from "@/lib/ai/visualization/data/schema";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";

/**
 * view type ごとの json-render component 名。
 */
type VizComponentTypeByViewType = {
  "bar-comparison": "BarComparison";
  "bar-ranking": "BarRanking";
  "scatter-plot": "ScatterPlot";
  table: "VizTable";
  "time-series-area": "TimeSeriesArea";
  "time-series-composed": "TimeSeriesComposed";
  "time-series-grouped-line": "TimeSeriesGroupedLine";
  "time-series-line": "TimeSeriesLine";
};

/**
 * strict view branch を説明するための最小 JSON 例。
 *
 * @remarks
 * example は runtime component props ではなく、authoring model が返す
 * root `view` shape に揃える。これにより prompt が renderer vocabulary
 * を再注入せず、strict union branch の最小構造だけを教えられる。
 */
type VizViewAuthoringPromptExample = {
  label: string;
  view: Record<string, unknown>;
};

/**
 * structured authoring で view-local に提示する semantic contract。
 *
 * @remarks
 * full `catalog.prompt()` は runtime contract を担うため、各 view が必要とする
 * field semantics や renderer-ready shape の注意点は `views/*` 配下に
 * コロケーションして SSOT に寄せる。
 */
type VizViewAuthoringPromptContract = {
  summary: string;
  requiredKeys: readonly string[];
  optionalKeys: readonly string[];
  selectionRules: readonly string[];
  avoidWhen: readonly string[];
  minimalExamples: readonly VizViewAuthoringPromptExample[];
};

type VizViewDatasetRequirements = {
  requiresTimeDimension: boolean;
  requiresCategoryDimension: boolean;
  minMetricCount: number;
  requiresCompareMode: boolean;
};

export type VizAuthoringCardDraft = {
  title?: string | null;
  description?: string | null;
  footnote?: string | null;
};

/**
 * compiled `VizSpec` が selected dataset と renderer contract に整合するかを検証する。
 *
 * @remarks
 * authoring schema は LLM 出力の構造だけを扱い、dataset との semantic 整合は
 * view definition 側の validator に集約する。
 */
export type ValidateVizSpecArgs<TSpec extends VizSpec = VizSpec> = {
  dataset: VizDataset;
  spec: TSpec;
};

export type VizViewDefinition<
  TViewType extends VizViewType = VizViewType,
  TAuthoringSchema extends z.ZodTypeAny = z.ZodTypeAny,
  TSpec extends VizSpec = VizSpec,
> = {
  type: TViewType;
  componentType: VizComponentTypeByViewType[TViewType];
  authoringSchema: TAuthoringSchema;
  authoringPrompt: VizViewAuthoringPromptContract;
  datasetRequirements: VizViewDatasetRequirements;
  validateSpec: (args: ValidateVizSpecArgs<TSpec>) => VizAuthoringIssue[];
  buildElement: (args: BuildVizElementArgs<TSpec>) => BuiltVizElement;
  compileSpec: (args: {
    datasetId: string;
    dataset: VizDataset;
    card: VizAuthoringCardDraft | null;
    view: z.infer<TAuthoringSchema>;
  }) => TSpec;
};

/**
 * view ごとに定義する authoring/compile/build 契約を型安全に束ねる。
 *
 * @remarks
 * 返り値は恒等関数のままにし、定義の重複だけを削減する。
 */
export function defineVizView<
  TViewType extends VizViewType,
  TAuthoringSchema extends z.ZodTypeAny,
  TSpec extends VizSpec,
>(
  definition: VizViewDefinition<TViewType, TAuthoringSchema, TSpec>,
): VizViewDefinition<TViewType, TAuthoringSchema, TSpec> {
  return definition;
}
